import { FC, useState } from 'react';
import { useGateways } from '../context/GatewayContext';
import { useWalletContext } from '../context/WalletContext';
import { BellIcon } from '@heroicons/react/24/solid';
import PageContainer from '../components/layout/PageContainer';
import NotificationItem from '../components/notifications/NotificationItem';
import { toast } from 'react-hot-toast';
import SrcfulMessageProtocolFactory from '../services/backend/SrcfulMessageProtocolFactory';

interface Notification {
  id: number;
  message: string;
  type: string;
  timestamp: number;
}

const NotificationsPage: FC = () => {
  const { isConnected, publicKey, wallet } = useWalletContext();
  const { gateways } = useGateways();
  const [deletingNotifications, setDeletingNotifications] = useState<Record<number, { timeoutId: NodeJS.Timeout }>>({});

  if (!isConnected) {
    return (
      <>
        <PageContainer>
          <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
            <div className="flex items-center gap-2 mb-2 sm:mb-4">
              <div className="flex items-center gap-2">
                <BellIcon className="w-5 h-5 text-[#00FF84]" />
                <h1 className="text-xl font-bold text-white">
                  Notifications
                </h1>
              </div>
            </div>
          </div>
          <div className="text-center py-12">
            <p className="text-gray-400">Please connect your wallet to view notifications</p>
          </div>
        </PageContainer>
      </>
    );
  }

  if (!gateways || gateways.length === 0) {
    return (
      <>
        <PageContainer>
          <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
            <div className="flex items-center gap-2 mb-2 sm:mb-4">
              <div className="flex items-center gap-2">
                <BellIcon className="w-5 h-5 text-[#00FF84]" />
                <h1 className="text-xl font-bold text-white">
                  Notifications
                </h1>
              </div>
            </div>
          </div>
          <div className="text-center py-12">
            <p className="text-gray-400">No gateways found. Add a gateway to receive notifications.</p>
          </div>
        </PageContainer>
      </>
    );
  }

  const handleDelete = async (id: number) => {
    if (!publicKey || !wallet) return;

    const timeoutId = setTimeout(() => {
      if (deletingNotifications[id]) {
        toast.error('Delete notification timed out');
        setDeletingNotifications(prev => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      }
    }, 30000);

    setDeletingNotifications(prev => ({
      ...prev,
      [id]: { timeoutId }
    }));

    const message = SrcfulMessageProtocolFactory.deleteNotificationMessage({
      walletPublicKey: publicKey.toString(),
      gatewayId: '', // We don't need this for the notifications page
      notificationId: id
    });

    try {
      const messageContext = await wallet.signMessage(message);
      if (messageContext?.signedMessage) {
        toast.success('Notification deleted');
        clearTimeout(timeoutId);
        setDeletingNotifications(prev => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      } else {
        throw new Error('Failed to sign message');
      }
    } catch (error) {
      toast.error('Failed to delete notification');
      clearTimeout(timeoutId);
      setDeletingNotifications(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  // Collect all notifications from all gateways
  const allNotifications = gateways.flatMap(gateway => 
    gateway.state?.status?.messages?.map((message: Notification) => ({
      ...message,
      gatewayId: gateway.id,
      gatewayName: gateway.name
    })) || []
  ).sort((a, b) => b.timestamp - a.timestamp);

  return (
    <>
      <PageContainer>
        <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
          <div className="flex items-center gap-2 mb-2 sm:mb-4">
            <div className="flex items-center gap-2">
              <BellIcon className="w-5 h-5 text-[#00FF84]" />
              <h1 className="text-xl font-bold text-white">
                Notifications
              </h1>
            </div>
          </div>
        </div>

        <p className="text-gray-400 mb-6">
          View and manage notifications for all your connected gateways.
        </p>

        <div className="space-y-4">
          {allNotifications.length > 0 ? (
            allNotifications.map((notification) => (
              <NotificationItem
                key={`${notification.gatewayId}-${notification.id}`}
                id={notification.id}
                type={notification.type}
                timestamp={notification.timestamp}
                message={notification.message}
                gatewayName={notification.gatewayName}
                onDelete={handleDelete}
                showDeleteButton={true}
              />
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400">No notifications available</p>
            </div>
          )}
        </div>
      </PageContainer>
    </>
  );
};

export default NotificationsPage; 